import {
  FileText,
  Settings,
  Calculator,
  GitBranch,
  BarChart3,
  Shield,
  Brain,
  FileSearch,
  MessageSquare,
  TrendingUp,
  AlertTriangle,
  Rocket,
  Crown,
  Building2,
  Users,
  Factory,
  Cog,
  Wrench,
} from 'lucide-react';

// ── Pricing Plans ──────────────────────────────────────────────
export const PLANS = [
  {
    name: 'Essentials',
    monthlyPrice: 25,
    yearlyPrice: 20,
    icon: Rocket,
    color: 'cyan',
    users: '1',
    rfqs: '250',
    highlights: [
      '1 User Included',
      '250 RFQs / Year',
      'CRM & Lead Management',
      'RFQ Capture & Tracking',
      'Basic BOM Management',
      'Costing Engine',
      'Customer Quote Generation',
      'Document Management',
      'Email Notifications',
      'Standard Reports',
      'Mobile Access',
    ],
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Professional',
    monthlyPrice: 100,
    yearlyPrice: 80,
    icon: Crown,
    color: 'purple',
    users: '5',
    rfqs: '1,000',
    highlights: [
      'Up to 5 Users',
      '1,000 RFQs / Year',
      'Everything in Essentials',
      'BOM Version Control',
      'Workflow & Multi-level Approvals',
      'Advanced Analytics & Dashboards',
      'Vendor Quote Management',
      'What-if Scenario Analysis',
      'Role-based Access Control',
      'Custom Report Builder',
      'API Access & ERP Integration',
      'Priority Support',
    ],
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    yearlyPrice: null,
    icon: Building2,
    color: 'gold',
    users: 'Unlimited',
    rfqs: 'Unlimited',
    highlights: [
      'Unlimited Users',
      'Unlimited RFQs',
      'Everything in Professional',
      'Full Platform Customization',
      'Dedicated Account Manager',
      'SLA Guarantee & 99.9% Uptime',
      'On-Premise Deployment Option',
      'SSO / SAML Integration',
      'Custom Workflows & Automation',
      'White-label Options',
      'Advanced Security & Compliance',
      'Training & Onboarding Support',
    ],
    cta: 'Contact Sales',
    popular: false,
  },
];

// ── Add-ons ────────────────────────────────────────────────────
export const ADDONS = [
  {
    name: 'AI Copilot Suite',
    icon: Brain,
    price: { monthly: 250, yearly: 250 },
    highlight: true,
    features: ['Drawing to BOM', 'Card to Lead', 'Notes to Minutes', 'Smart Analytics'],
  },
];

// ── Platform Stats ─────────────────────────────────────────────
export const PLATFORM_STATS = [
  { value: '50-70%', label: 'Faster Quotations' },
  { value: '100%', label: 'Data Centralization' },
  { value: '0%', label: 'Manual Errors' },
  { value: '50+', label: 'Happy Customers' },
];

export const TRUST_STATS = [
  { icon: Building2, value: '50+', label: 'Manufacturers' },
  { icon: Users, value: '500+', label: 'Active Users' },
  { icon: Shield, value: '100%', label: 'Secure' },
];

// ── Customer Logos ─────────────────────────────────────────────
export const CUSTOMER_LOGOS = [
  { name: 'Rockman', logo: '/1.jpg' },
  { name: 'Bony', logo: '/2.jpg' },
  { name: 'Metalman', logo: '/3.jpg' },
  { name: 'Mega', logo: '/4.jpg' },
  { name: 'Velangi Connectivity', logo: '/5.jpg' },
  { name: 'Polyrub', logo: '/6.jpg' },
  { name: 'Mega Hefa Emission Technologies', logo: '/7.jpg' },
  { name: 'Atmos', logo: '/8.jpg' },
  { name: "Katariya's Sheet Shapers", logo: '/9.jpg' },
  { name: 'Cooper Standard', logo: '/10.jpg' },
  { name: 'Sujan Continental', logo: '/11.jpg' },
  { name: 'National', logo: '/12.jpg' },
  { name: 'MMT Autocomp', logo: '/13.png' },
  { name: 'Dhananjay', logo: '/14.jpg' },
  { name: 'MK Polymers', logo: '/15.jpg' },
  { name: 'Client 16', logo: '/16.jpg' },
];

// ── Feature Categories ─────────────────────────────────────────
export const FEATURE_CATEGORIES = [
  {
    id: 'rfq',
    name: 'RFQ Management',
    icon: FileText,
    tagline: 'From chaos to clarity',
    description: 'All customer RFQs in one place with automatic categorization, smart routing, and SLA tracking.',
    capabilities: [
      'Centralized RFQ Inbox',
      'Real-time Status Tracking',
      'Document Management',
      'Auto-categorization & Smart Routing',
      'SLA Monitoring & Escalation Alerts',
    ],
  },
  {
    id: 'bom',
    name: 'BOM Management',
    icon: Settings,
    tagline: 'Structure meets intelligence',
    description: 'Multi-level BOM management with parent-child relationships, version control, and technical feasibility workflows.',
    capabilities: [
      'Hierarchical BOM Structure',
      'Automatic Versioning & Comparison',
      'Technical Feasibility Assessment',
      'Component Dependency Mapping',
      'Change Tracking & Risk Flagging',
    ],
  },
  {
    id: 'costing',
    name: 'Costing Engine',
    icon: Calculator,
    tagline: 'Precision at every level',
    description: 'Automatic cost calculation using predefined norms, real-time material pricing, and what-if scenario modeling.',
    capabilities: [
      'Norms-based Cost Calculation',
      'Material Cost & Rate Cards',
      'What-if Scenario Analysis',
      'Margin Optimization',
      'Supplier Rate History',
    ],
  },
  {
    id: 'workflow',
    name: 'Workflow & Approvals',
    icon: GitBranch,
    tagline: 'Your process, automated',
    description: 'Drag-and-drop workflow configuration with role-based approvals, conditional routing, and mobile access.',
    capabilities: [
      'Visual Workflow Builder',
      'Role-based Approval Routing',
      'Conditional & Parallel Approvals',
      'Mobile-friendly Approvals',
      'Push Notifications & Quick Actions',
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics & Insights',
    icon: BarChart3,
    tagline: 'Data-driven decisions',
    description: 'Live KPI dashboards, deep performance analytics, and drag-and-drop custom report builder.',
    capabilities: [
      'Real-time KPI Dashboards',
      'Win/Loss & Trend Analysis',
      'Custom Report Builder',
      'Scheduled Reports & Exports',
      'Benchmark Comparison',
    ],
  },
  {
    id: 'security',
    name: 'Security & Compliance',
    icon: Shield,
    tagline: 'Enterprise-grade protection',
    description: 'Granular role-based access control, AES-256 encryption, and built-in GDPR/SOC 2 compliance.',
    capabilities: [
      'Role-based Access Control',
      'AES-256 Data Encryption',
      'GDPR & SOC 2 Compliance',
      'Complete Audit Trails',
      'SSO/SAML Support',
    ],
  },
];

// ── AI Capabilities ────────────────────────────────────────────
export const AI_CAPABILITIES = [
  {
    icon: FileSearch,
    title: 'AI Card Reading & Extraction',
    shortTitle: 'Card to Lead',
    description: 'Automatically extract data from business cards, purchase orders, and documents with intelligent OCR.',
    features: ['Smart document OCR', 'Table & form extraction', 'Multi-format support', 'Data validation & mapping'],
    color: '#00D4FF',
  },
  {
    icon: Settings,
    title: 'AI Drawing-to-BOM',
    shortTitle: 'Drawing to BOM',
    description: 'Convert 2D engineering drawings and CAD files into structured BOM data with intelligent component recognition.',
    features: ['CAD file parsing', 'Component identification', 'Dimension extraction', 'Material detection'],
    color: '#3B82F6',
  },
  {
    icon: MessageSquare,
    title: 'AI Meeting Minutes',
    shortTitle: 'Notes to Minutes',
    description: 'Capture meeting discussions into actionable items with automatic task assignment and follow-ups.',
    features: ['Speech-to-text conversion', 'Action item extraction', 'Decision logging', 'Follow-up automation'],
    color: '#8B5CF6',
  },
  {
    icon: TrendingUp,
    title: 'AI Business Analytics',
    shortTitle: 'Smart Analytics',
    description: 'Predictive insights on win rates, pricing optimization, and market trends with machine learning.',
    features: ['Win/loss prediction', 'Price optimization', 'Trend analysis', 'Market intelligence'],
    color: '#F59E0B',
  },
  {
    icon: AlertTriangle,
    title: 'AI Risk & Margin Intelligence',
    shortTitle: 'Risk Alerts',
    description: 'Automatic risk flagging and margin analysis to help you make better pricing decisions.',
    features: ['Risk scoring', 'Margin analysis', 'Alert generation', 'Recommendation engine'],
    color: '#EF4444',
  },
  {
    icon: BarChart3,
    title: 'AI Reports & Dashboards',
    shortTitle: 'Smart Reports',
    description: 'Natural language queries and AI-generated insights for management reporting.',
    features: ['Natural language queries', 'Auto-generated insights', 'Anomaly detection', 'Smart summaries'],
    color: '#14B8A6',
  },
];

// ── Industry Solutions ─────────────────────────────────────────
export const INDUSTRY_SOLUTIONS = [
  {
    id: 'auto-components',
    icon: Factory,
    title: 'Auto-Component Manufacturers',
    subtitle: 'Tier 1, 2, 3 suppliers managing high-volume RFQs from OEMs',
    description: 'Streamline your quotation process for automotive components with specialized workflows designed for OEM requirements, complex BOMs, and tight delivery timelines.',
    challenges: [
      'High RFQ volume with tight OEM deadlines',
      'Complex multi-level BOMs with frequent revisions',
      'Customer-specific quote formats and compliance',
      'Multi-plant coordination and resource planning',
    ],
    solutions: [
      'Automated RFQ categorization and routing',
      'Multi-level BOM management with version control',
      'OEM-specific quote templates and formats',
      'Cross-plant visibility and collaboration',
    ],
    results: [
      { metric: '65%', label: 'Faster Quotes' },
      { metric: '40%', label: 'Fewer Errors' },
      { metric: '100%', label: 'OEM Compliance' },
    ],
  },
  {
    id: 'fabrication',
    icon: Cog,
    title: 'Fabrication & Sheet Metal',
    subtitle: 'Custom fabrication, plastic molding, rubber parts, and casting',
    description: 'Handle process-specific costing complexities with intelligent norms libraries, material yield calculations, and tooling cost management.',
    challenges: [
      'Process-specific pricing complexity',
      'Material yield and scrap calculations',
      'Tooling cost amortization',
      'Quick-turn quotation demands',
    ],
    solutions: [
      'Process-specific costing templates',
      'Automatic yield and scrap calculation',
      'Tooling lifecycle and amortization tracking',
      'Rapid quote generation engine',
    ],
    results: [
      { metric: '70%', label: 'Faster Costing' },
      { metric: '95%', label: 'Quote Accuracy' },
      { metric: '50%', label: 'Less Manual Work' },
    ],
  },
  {
    id: 'tool-die',
    icon: Wrench,
    title: 'Tool & Die Manufacturers',
    subtitle: 'Precision tooling with complex development cycles',
    description: 'Manage long project timelines, development phases, and trial allocations with comprehensive tooling lifecycle tracking.',
    challenges: [
      'Long project timelines with multiple phases',
      'Trial and development cost allocation',
      'Design revision management',
      'Customer approval workflows',
    ],
    solutions: [
      'Project-based quotation management',
      'Phase-wise costing and tracking',
      'Design revision control',
      'Customer portal for approvals',
    ],
    results: [
      { metric: '60%', label: 'Better Visibility' },
      { metric: '100%', label: 'Revision Tracking' },
      { metric: '45%', label: 'Faster Approvals' },
    ],
  },
  {
    id: 'engineering',
    icon: Building2,
    title: 'Engineering & EPC',
    subtitle: 'Project-based manufacturing with engineering complexity',
    description: 'Coordinate multi-discipline teams, manage change orders, and track project profitability with integrated project management.',
    challenges: [
      'Multi-discipline coordination',
      'Change order management',
      'Subcontractor cost consolidation',
      'Project profitability tracking',
    ],
    solutions: [
      'Cross-functional workflow coordination',
      'Change order tracking and impact analysis',
      'Consolidated vendor quote management',
      'Real-time project profitability dashboards',
    ],
    results: [
      { metric: '55%', label: 'Better Coordination' },
      { metric: '100%', label: 'Change Tracking' },
      { metric: '35%', label: 'Improved Margins' },
    ],
  },
];

// ── Complete Feature List ──────────────────────────────────────
export const ALL_FEATURES = [
  'RFQ Capture & Tracking',
  'BOM Version Control',
  'Smart Quote Engine',
  'Customer Quote Generation',
  'Vendor Quote Management',
  'APQP Governance',
  'Multi-level Approvals',
  'Complete Audit Trail',
  'Real-time Dashboards',
  'AI-powered Analytics',
  'ERP/SAP Integration',
  'Role-based Access',
  'SSO/SAML Support',
  'Mobile App Access',
  'Custom Workflows',
  'Report Builder',
  'Email Integration',
  'API Access',
];

// ── Company Info ───────────────────────────────────────────────
export const COMPANY_INFO = {
  // address: 'No 706 Block-E 7th floor, Dwarka Sector 19B, (Opposite Dwarka Golf Course) New Delhi 110075',
  address: '5th Floor, Awfis, NH- 8, Ambience Island, DLF Phase 3, Sector 24, Gurugram, Haryana 122010',
  email: 'info@costgenie.com',
  phone: '+91-805 508 2222',
  whatsapp: 'https://wa.me/+918055082222',
  linkedin: 'https://www.linkedin.com/company/excelleta/',
  facebook: 'https://www.facebook.com/people/Excelleta-Tech/61550919895618/',
  tagline: 'AI-Powered CRM, RFQ, BOM & Quote Platform for Manufacturers | Faster Quotes. Better Margins. Complete Visibility.',
};

// Free plan id
export const FREE_PLAN_ID = '69c982fa34f86f10fd923129';
// ── External Links ──────────────────────────────────────────
export const REGISTER_URL = `https://admin-alpha.costgenie.com/register?planId=${FREE_PLAN_ID}`;
export const LOGIN_URL = `https://quote-alpha.costgenie.com/auth/login`;